import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api-client.js';

const STATUS_COLORS: Record<string, string> = {
  lead: 'bg-blue-500/20 text-blue-400',
  negotiating: 'bg-yellow-500/20 text-yellow-400',
  pending: 'bg-orange-500/20 text-orange-400',
  finance_pending: 'bg-purple-500/20 text-purple-400',
  ready_to_sign: 'bg-cyan-500/20 text-cyan-400',
  signed: 'bg-green-500/20 text-green-400',
  funded: 'bg-green-500/20 text-green-400',
  delivered: 'bg-green-500/20 text-green-400',
  completed: 'bg-emerald-500/20 text-emerald-400',
  lost: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-surface-700 text-surface-400',
};

function fmt(cents: number | null | undefined): string {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export default function DealsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'list' | 'pipeline' | 'new'>('list');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // List
  const { data: listData, isLoading } = useQuery({
    queryKey: ['deals', statusFilter, search],
    queryFn: () => api.get<any[]>(`/deals?status=${statusFilter}&search=${encodeURIComponent(search)}&limit=100`),
    enabled: tab === 'list',
  });

  // Pipeline
  const { data: pipelineData } = useQuery({
    queryKey: ['deals-pipeline'],
    queryFn: () => api.get<any>('/deals/pipeline'),
    enabled: tab === 'pipeline',
  });

  // Stats
  const { data: statsData } = useQuery({
    queryKey: ['deals-stats'],
    queryFn: () => api.get<any>('/deals/stats/summary'),
  });

  const stats = statsData?.data;

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-surface-700 rounded w-48" />
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-surface-700 rounded-xl" />)}
        </div>
      </div>
    );
  }
  return (
    <div className="page">
      {/* Header + Stats */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Deals</h2>
        <button onClick={() => setTab('new')} className="btn-primary text-sm px-4 py-2">+ New Deal</button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="card p-3 text-center">
            <p className="text-lg font-bold text-brand-400">{stats.month?.deals_closed ?? 0}</p>
            <p className="text-xs text-surface-500">This Month</p>
          </div>
          <div className="card p-3 text-center">
            <p className="text-lg font-bold text-green-400">{fmt(stats.month?.revenue_cents)}</p>
            <p className="text-xs text-surface-500">Revenue</p>
          </div>
          <div className="card p-3 text-center">
            <p className="text-lg font-bold text-yellow-400">{fmt(stats.month?.gross_cents)}</p>
            <p className="text-xs text-surface-500">Gross Profit</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {(['list', 'pipeline'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm capitalize ${tab === t ? 'bg-brand-500 text-white' : 'bg-surface-800 text-surface-400'}`}
          >{t}</button>
        ))}
      </div>

      {/* List View */}
      {tab === 'list' && (
        <>
          <div className="flex gap-2 mb-4">
            <input
              placeholder="Search deals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input flex-1"
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input w-40">
              <option value="">All Status</option>
              {Object.entries(STATUS_COLORS).map(([k]) => (
                <option key={k} value={k}>{k.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            {(listData?.data ?? []).map((deal: any) => (
              <Link key={deal.id} to={`/deals/${deal.id}`} className="card p-4 block hover:bg-surface-700 transition-colors">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{deal.year} {deal.make} {deal.model}</p>
                    <p className="text-sm text-surface-500">{deal.deal_number} | {deal.customer_name}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[deal.status] ?? ''}`}>
                      {deal.status?.replace('_', ' ')}
                    </span>
                    <p className="text-sm font-bold mt-1">{fmt(deal.sale_price_cents)}</p>
                    {deal.salesperson_name && <p className="text-xs text-surface-500">{deal.salesperson_name}</p>}
                  </div>
                </div>
              </Link>
            ))}
            {(listData?.data ?? []).length === 0 && (
              <p className="text-center text-surface-500 py-8">No deals found.</p>
            )}
          </div>
        </>
      )}

      {/* Pipeline / Kanban View */}
      {tab === 'pipeline' && pipelineData?.data?.pipeline && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {['lead', 'negotiating', 'pending', 'finance_pending', 'ready_to_sign', 'signed', 'funded', 'delivered'].map((status) => {
              const deals = pipelineData.data.pipeline[status] ?? [];
              return (
                <div key={status} className="w-64 flex-shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[status] ?? ''}`}>
                      {status.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-surface-500">{deals.length}</span>
                  </div>
                  <div className="space-y-2">
                    {deals.map((deal: any) => (
                      <Link key={deal.id} to={`/deals/${deal.id}`} className="card p-3 block hover:bg-surface-700 transition-colors">
                        <p className="font-medium text-sm">{deal.year} {deal.make} {deal.model}</p>
                        <p className="text-xs text-surface-500">{deal.customer_name}</p>
                        <p className="text-sm font-bold text-brand-400 mt-1">{fmt(deal.sale_price_cents)}</p>
                      </Link>
                    ))}
                    {deals.length === 0 && (
                      <div className="text-center text-surface-600 text-xs py-4 border border-dashed border-surface-700 rounded-lg">Empty</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* New Deal Flow */}
      {tab === 'new' && <NewDealFlow onCreated={(id) => { navigate(`/deals/${id}`); setTab('list'); }} onCancel={() => setTab('list')} />}
    </div>
  );
}

// ── New Deal Flow ────────────────────────────────
function NewDealFlow({ onCreated, onCancel }: { onCreated: (id: string) => void; onCancel: () => void }) {
  const [step, setStep] = useState(1);
  const [vehicleId, setVehicleId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [paymentType, setPaymentType] = useState('cash');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');

  // Search vehicles
  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles-search', vehicleSearch],
    queryFn: () => api.get<any[]>(`/vehicles?search=${encodeURIComponent(vehicleSearch)}&status=active&limit=20`),
    enabled: step === 1 && vehicleSearch.length > 0,
  });

  // Search customers
  const { data: customersData } = useQuery({
    queryKey: ['customers-search', customerSearch],
    queryFn: () => api.get<any[]>(`/customers?search=${encodeURIComponent(customerSearch)}&limit=20`),
    enabled: step === 2 && customerSearch.length > 0,
  });

  const createDeal = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post<{ id: string }>('/deals', data),
    onSuccess: (res) => { if (res.data?.id) onCreated(res.data.id); },
  });

  return (
    <div className="card p-6 border border-brand-500/30">
      <h3 className="text-lg font-semibold mb-4">
        New Deal — Step {step} of 3
      </h3>

      {/* Step 1: Select Vehicle */}
      {step === 1 && (
        <div>
          <label className="text-sm text-surface-500 mb-2 block">Select Vehicle</label>
          <input
            placeholder="Search by VIN, make, model..."
            value={vehicleSearch}
            onChange={(e) => setVehicleSearch(e.target.value)}
            className="input mb-3"
          />
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(vehiclesData?.data ?? []).map((v: any) => (
              <button
                key={v.id}
                onClick={() => { setVehicleId(v.id); setSalePrice(String((v.asking_price_cents ?? 0) / 100)); setStep(2); }}
                className={`w-full card p-3 text-left hover:bg-surface-700 ${vehicleId === v.id ? 'border-brand-500' : ''}`}
              >
                <p className="font-medium">{v.year} {v.make} {v.model}</p>
                <p className="text-sm text-surface-500">{v.vin} | {v.mileage?.toLocaleString()} mi | ${((v.asking_price_cents ?? 0) / 100).toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Select Customer */}
      {step === 2 && (
        <div>
          <label className="text-sm text-surface-500 mb-2 block">Select Customer</label>
          <input
            placeholder="Search by name, phone, email..."
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            className="input mb-3"
          />
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(customersData?.data ?? []).map((c: any) => (
              <button
                key={c.id}
                onClick={() => { setCustomerId(c.id); setStep(3); }}
                className={`w-full card p-3 text-left hover:bg-surface-700 ${customerId === c.id ? 'border-brand-500' : ''}`}
              >
                <p className="font-medium">{c.name}</p>
                <p className="text-sm text-surface-500">{c.phone ?? c.email ?? ''}</p>
              </button>
            ))}
          </div>
          <button onClick={() => setStep(1)} className="text-sm text-brand-400 mt-3">&larr; Back</button>
        </div>
      )}

      {/* Step 3: Pricing */}
      {step === 3 && (
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-surface-500">Sale Price ($)</label>
              <input type="number" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} className="input" />
            </div>
            <div>
              <label className="text-xs text-surface-500">Payment Type</label>
              <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} className="input">
                <option value="cash">Cash</option>
                <option value="finance">Finance</option>
                <option value="outside_finance">Outside Finance</option>
                <option value="lease">Lease</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => createDeal.mutate({
                vehicle_id: vehicleId,
                customer_id: customerId,
                sale_price_cents: Math.round(parseFloat(salePrice) * 100),
                payment_type: paymentType,
              })}
              disabled={createDeal.isPending}
              className="btn-primary px-6 py-2"
            >{createDeal.isPending ? 'Creating...' : 'Create Deal'}</button>
            <button onClick={() => setStep(2)} className="btn-secondary px-4 py-2">&larr; Back</button>
            <button onClick={onCancel} className="text-sm text-surface-500">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
